<script lang="ts">
  import Icons from "$lib/main/components/Icons.svelte";
  import Translate from "$lib/admin/components/blocks/helpers/Translate.svelte";
  import { project, projectAllToplists } from "$lib/services/store";
  import { searchClient as meilisearchClient } from "$lib/services/meilisearch/client";
  import { Button, Modal } from "flowbite-svelte";
  import { InstantSearch, SearchBox, Hits, Index, Configure } from "svelte-meilisearch-instantsearch";
  import { darkenRgbColor } from "$lib/common/helpers/colors";

  interface CommercialToplist {
    id: number;
    toplist: number[] | object;
  }

  let defaultSlots: number[] = [];
  let defaultArticles: number[] = [];
  let initialSearchOperatorIds: number[] = [];
  const defaultCommercialId: number | undefined = $project.data.search?.defaultCommercial;

  $: {
    if ($projectAllToplists && defaultCommercialId) {
      const commercial = ($projectAllToplists as CommercialToplist[]).find((c) => c.id === defaultCommercialId);

      if (commercial && Array.isArray(commercial.toplist)) {
        initialSearchOperatorIds = commercial.toplist;
      } else {
        initialSearchOperatorIds = [];
      }
    }

    if ($project.data.search?.defaultArticles) {
      defaultArticles = $project.data.search.defaultArticles;
    }

    if ($project.data.search?.defaultSlots) {
      defaultSlots = $project.data.search.defaultSlots;
    }
  }

  const market = $project.settings.market;
  const projectKey = $project.key;
  const operatorsImageUrl = $project.data.imageFolders?.operators;
  const slotsImageUrl = $project.data.imageFolders?.media;

  let currentQuery = "";
  let showModal = false;

  function openSearchModal(): void {
    showModal = true;
    document.body.classList.add("no-scroll");
    currentQuery = "";
    lastQuery = "";

    // TODO: Remove this when we have a better solution
    // This is a temporary fix because of the "contain: paint" property on the .t-cf class on the theme-cf.css file
    window.scrollTo({ top: 0, behavior: "auto" });

    setTimeout(() => {
      const searchInput = document.querySelector(".ais-SearchBox-input");
      if (searchInput instanceof HTMLElement) {
        searchInput.focus();
      }
    }, 100);
  }

  function closeSearchModal(): void {
    showModal = false;
    currentQuery = "";
    document.body.classList.remove("no-scroll");
  }

  let lastQuery = "";

  const queryHook = (query: string, hook: (value: string) => void) => {
    const isLikelyReplacement =
      // Case 1: Single character that's completely different from previous query
      (lastQuery && query.length === 1 && !lastQuery.startsWith(query) && !query.startsWith(lastQuery)) ||
      // Case 2: Query length suddenly dropped to 1 (likely selection + replacement)
      (lastQuery && lastQuery.length > 1 && query.length === 1) ||
      // Case 3: New query starts with same character but is much shorter
      (lastQuery && query.length === 1 && lastQuery.startsWith(query) && lastQuery.length > 1);

    if (isLikelyReplacement) {
      // For text replacement, skip debounce
      currentQuery = query;
      hook(query);
      lastQuery = query;
      return () => {};
    }

    currentQuery = query;
    lastQuery = query;

    const timerId = setTimeout(() => {
      if (query.length >= 2) {
        hook(query);
      } else {
        hook("");
      }
    }, 30);

    return () => clearTimeout(timerId);
  };

  let hitCounts = {
    operators: 0,
    news: 0,
    slots: 0,
  };

  function createTransformItemsUpdater(indexName: string) {
    return function (items: any[]) {
      hitCounts = { ...hitCounts, [indexName]: items.length };

      // Re-sort the operators to match commercial ordering
      if (indexName === "operators" && currentQuery.length < 2 && initialSearchOperatorIds.length > 0) {
        const orderMap = new Map(initialSearchOperatorIds.map((id, index) => [id, index]));
        return [...items].sort((a, b) => {
          const orderA = orderMap.get(a.id);
          const orderB = orderMap.get(b.id);
          if (orderA === undefined) return 1;
          if (orderB === undefined) return -1;
          return orderA - orderB;
        });
      }

      return items;
    };
  }

  const searchClientProps = {
    searchClient: meilisearchClient,
  };

  // Sorry, little hack to get around the typescript error
  const InstantSearchComponent = InstantSearch as any;
</script>

<div class="flex gap-2">
  <Button class="bg-transparent hover:bg-transparent focus:border-0 focus:ring-0" on:click={openSearchModal}>
    <Icons name="search" height="20px" width="20px" strokeWidth="3" color="#fff" />
  </Button>
</div>

<!-- Search modal -->
<Modal
  bind:open={showModal}
  on:close={closeSearchModal}
  size="xl"
  outsideclose={true}
  placement="top-center"
  class="p-0">
  <div class="overflow-y-auto max-h-[88vh] md:max-h-[75vh] !mt-10">
    <InstantSearchComponent {...searchClientProps} indexName="operators">
      <SearchBox
        placeholder="Sök"
        {queryHook}
        classes={{
          root: "w-full p-1 mb-4",
          form: "relative",
          input:
            "h-12 pl-8 rounded-sm w-full [&::-webkit-search-cancel-button]:hidden focus:border-brand focus:ring-brand",
          reset: "absolute top-1/2 left-4 transform -translate-y-1/2",
          submit:
            "absolute top-1/2 right-1 transform -translate-y-1/2 bg-brand hover:bg-brand-light transition-all duration-300 ease-in-out w-10 h-10 flex items-center justify-center rounded-sm",
          submitIcon: "fill-white w-5 h-5",
          loadingIndicator: "absolute top-1/2 left-3 transform -translate-y-1/2 bg-white",
        }} />

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3 overflow-y-auto">
        {#if true}
          <!-- Operators search results -->
          <div class="bg-gray-100 p-4 rounded-lg">
            <h3>
              <Translate text="Casinos" />
            </h3>
            <Index indexName="operators">
              {#if currentQuery.length < 2 && initialSearchOperatorIds.length > 0}
                <Configure
                  hitsPerPage={initialSearchOperatorIds.length || 20}
                  filters={`id IN [${initialSearchOperatorIds.join(", ")}]`} />
              {:else}
                <Configure hitsPerPage={5} />
              {/if}
              <Hits
                let:hit
                classes={{ list: "flex flex-col gap-4" }}
                transformItems={createTransformItemsUpdater("operators")}>
                <a
                  class="flex flex-col hover:scale-[104%] transition-all duration-200 ease-in-out"
                  href="/svenska-casinon/{hit.name_lower}">
                  {#if hit.logo}
                    <div
                      class="flex items-center h-16 py-2 gap-2 rounded-t-md rounded-b-none justify-center"
                      style="background-color: rgba({hit.visuals[projectKey][market].primary_color})">
                      <img
                        src="{operatorsImageUrl}/{hit.logo[projectKey]}?w=184&q=80&auto=format"
                        alt="{hit.name} logo"
                        class="w-42 max-h-16 h-full rounded-xl object-contain p-1" />
                    </div>
                  {/if}
                  <div
                    class="flex rounded-t-none rounded-b-md items-center justify-center p-1 text-sm font-light text-black"
                    style="margin-top: -1px; color: rgba({hit.visuals[projectKey][market]
                      .secondary_color}); background-image: linear-gradient(to bottom, rgba({hit.visuals[projectKey][
                      market
                    ].primary_color}) 0%, rgba({darkenRgbColor(
                      hit.visuals[projectKey][market].primary_color,
                      30,
                    )}) 100%);">
                    {#if hit.bonus?.casino?.[market].bonus_amount}
                      {hit.bonus.casino[market].bonus_amount} {hit.bonus.casino[market].currency} Bonus
                    {:else if hit.bonus?.casino?.[market].bonus_fallback}
                      {hit.bonus.casino[market].bonus_fallback}
                    {:else if hit.bonus?.casino?.[market].freespins_amount}
                      {hit.bonus.casino[market].freespins_amount} Free Spins
                    {:else}
                      {hit.bonus?.casino?.[market].freespins_fallback}
                    {/if}
                  </div>
                </a>
              </Hits>
              {#if currentQuery.length >= 2 && hitCounts.operators === 0}
                <div class="no-results p-4 text-center text-gray-500 text-md">
                  <div class="flex justify-center items-center mb-2">
                    <Icons width="48" height="48" color="lightgrey" name="search-no-results" />
                  </div>
                  <Translate text="Found no casinos for" /> "{currentQuery}"
                </div>
              {/if}
            </Index>
          </div>

          <!-- News search results -->
          <div class="bg-gray-100 p-4 rounded-lg">
            <h3>
              <Translate text="Articles" />
            </h3>
            <Index indexName="news">
              {#if currentQuery.length < 2 && defaultArticles.length > 0}
                <Configure hitsPerPage={18} filters={`id IN [${defaultArticles.join(", ")}]`} />
              {:else}
                <Configure hitsPerPage={18} />
              {/if}
              <Hits
                let:hit
                classes={{ list: "flex flex-col gap-2 text-md", item: "p-0" }}
                transformItems={createTransformItemsUpdater("news")}>
                <a href={hit.slug}>
                  <div class="content">
                    <h4 class="text-sm font-normal truncate dark:text-white" title={hit.title}>{hit.title}</h4>
                    {#if hit.description}
                      <p>{hit.description}</p>
                    {/if}
                  </div>
                </a>
              </Hits>
              {#if currentQuery.length >= 2 && hitCounts.news === 0}
                <div class="no-results p-4 text-center text-gray-500 text-md">
                  <div class="flex justify-center items-center mb-2">
                    <Icons width="48" height="48" color="lightgrey" name="search-no-results" />
                  </div>
                  <Translate text="Found no articles for" /> "{currentQuery}"
                </div>
              {/if}
            </Index>
          </div>

          <!-- Slots search results -->
          <div class="bg-gray-100 p-4 rounded-lg">
            <h3>
              <Translate text="Slots" />
            </h3>
            <Index indexName="slots">
              {#if currentQuery.length < 2 && defaultSlots.length > 0}
                <Configure hitsPerPage={10} filters={`id IN [${defaultSlots.join(", ")}]`} />
              {:else}
                <Configure hitsPerPage={10} />
              {/if}
              <Hits
                let:hit
                classes={{
                  list: "flex flex-col gap-2 text-md",
                  item: "p-0 [&_a:hover_img]:scale-105",
                }}
                transformItems={createTransformItemsUpdater("slots")}>
                <a href={hit.slug} class="flex items-center gap-2 hover:bg-gray-100 rounded-lg p-1 w-full text-sm">
                  <img
                    src={slotsImageUrl + hit.image_featured + "?w=184&q=80&auto=format"}
                    alt={hit.title}
                    class="w-24 max-h-24 rounded-xl object-contain transition-all duration-200 ease-in-out" />
                  <span class="truncate">{hit.title}</span>
                  {#if hit.provider}
                    <p class="text-sm text-gray-500 ml-auto pl-1 whitespace-nowrap">{hit.provider}</p>
                  {/if}
                </a>
              </Hits>
              {#if currentQuery.length >= 2 && hitCounts.slots === 0}
                <div class="no-results p-4 text-center text-gray-500 text-md">
                  <div class="flex justify-center items-center mb-2">
                    <Icons width="48" height="48" color="lightgrey" name="search-no-results" />
                  </div>
                  <Translate text="Found no slots for" /> "{currentQuery}"
                </div>
              {/if}
            </Index>
          </div>
        {/if}
      </div>
    </InstantSearchComponent>
  </div>
</Modal>
